import { DragMoveEvent, useDndMonitor } from "@dnd-kit/core";
import {
  memo,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useRef,
} from "react";
import { useOverDrag } from "hooks/useOverDrag";
import { store, useAppDispatch } from "stores";
import {
  incrementTransform,
  updateTransform,
} from "stores/modules/entities.room.transform/slice";
import { moveRoomObjects } from "stores/modules/entities.rooms/operations";
import { fixSelectionOnDragStop } from "modules/drag";
import { SelectedDragItem } from ".";
import { getSelectedObjectsPosition } from "stores/modules/entities.rooms/selectors";

type TransformContextProps = PropsWithChildren<{
  viewRef: MutableRefObject<HTMLDivElement | null>;
  centerRef: MutableRefObject<HTMLDivElement | null>;
  onDragEnd: (dragEnd: () => void) => void;
}>;

const TransformProvider = ({
  viewRef,
  centerRef,
  onDragEnd,
  children,
}: TransformContextProps) => {
  const dispatch = useAppDispatch();

  const x = useRef<number>(0);
  const y = useRef<number>(0);
  const currentItems = useRef<SelectedDragItem[]>([]);

  const onDrag = useCallback(
    (result) => {
      const overX = result.right || -result.left;
      const overY = result.bottom || -result.top;

      x.current += overX;
      y.current += overY;

      dispatch(incrementTransform({ x: overX, y: overY }));
    },
    [dispatch]
  );
  const { onDragOver, onDragOverStop } = useOverDrag(
    viewRef.current,
    centerRef.current,
    onDrag
  );

  const handleDragMove = useCallback(
    (e: DragMoveEvent) => {
      const { delta } = e;
      const event = e.activatorEvent as MouseEvent;
      onDragOver(event.clientX + delta.x, event.clientY + delta.y);
      dispatch(
        updateTransform({ x: delta.x + x.current, y: delta.y + y.current })
      );
    },
    [onDragOver, dispatch]
  );

  const handleDragStart = useCallback(() => {
    const state = store.getState();
    const items = getSelectedObjectsPosition(state);
    currentItems.current = items;
  }, []);

  useDndMonitor({
    onDragStart() {
      handleDragStart();
    },
    onDragMove(e) {
      handleDragMove(e);
    },
    onDragEnd(e) {
      onDragEnd(() => {
        onDragOverStop();
        const { active, delta } = e;
        const data = active.data.current;
        if (data?.onDragEnd) {
          data?.onDragEnd();
        }
        if (delta.x || delta.y || x.current || y.current) {
          const result = {
            x: delta.x + x.current,
            y: delta.y + y.current,
          };
          // アイテムごとのonUpdateをやめて選択済みアイテム全てに伝播させる
          dispatch(moveRoomObjects(currentItems.current, result));
        }
        fixSelectionOnDragStop();
        x.current = 0;
        y.current = 0;
      });
    },
  });

  return <>{children}</>;
};

export default memo(TransformProvider);
